import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  createFormActions,
  createFormEndpoint,
  createFormI18n,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';

/**
 * Returns the EntityForm configuration for Workflow Schedule create/edit forms.
 *
 * @param mode - Whether the form is for creating or editing a schedule.
 * @param scheduleId - The schedule ID (required in edit mode for fetching data).
 */
export function getWorkflowScheduleFormConfig(
  mode: 'create' | 'edit',
  scheduleId?: string,
): EntityFormConfig {
  const isEdit = mode === 'edit';

  return {
    id: isEdit
      ? createFormEditId('workflow-schedule', scheduleId)
      : createFormCreateId('workflow-schedule'),

    endpoint: createFormEndpoint(
      isEdit,
      '/api/admin/workflows/schedules',
      `/api/admin/workflows/schedules/${scheduleId}`,
      !!scheduleId,
      [
        'workflow_definition_id',
        'name',
        'description',
        'cron_expression',
        'timezone',
        'is_active',
      ],
    ),

    fields: {
      workflow_definition_id: {
        type: 'text',
        label: 'Workflow Definition ID',
        placeholder: 'e.g. 01234567-89ab-cdef-0123-456789abcdef',
        order: 1,
        section: 'basic',
        validation: {
          required: true,
        },
      },
      name: {
        type: 'text',
        label: 'Name',
        placeholder: 'e.g. Daily Sync',
        order: 2,
        section: 'basic',
        validation: {
          required: true,
          max: { value: 255, message: 'Name must be at most 255 characters' },
        },
      },
      description: {
        type: 'textarea',
        label: 'Description',
        placeholder: 'Optional description of the schedule',
        order: 3,
        section: 'basic',
        validation: {
          max: { value: 1000, message: 'Description must be at most 1000 characters' },
        },
      },
      cron_expression: {
        type: 'text',
        label: 'Cron Expression',
        placeholder: 'e.g. 0 */6 * * *',
        helperText: 'Standard cron expression (minute hour day month weekday)',
        order: 4,
        section: 'scheduling',
        validation: {
          required: true,
          max: { value: 255, message: 'Cron expression must be at most 255 characters' },
        },
      },
      timezone: {
        type: 'text',
        label: 'Timezone',
        placeholder: 'e.g. UTC',
        helperText: 'IANA timezone identifier',
        order: 5,
        section: 'scheduling',
        validation: {
          max: { value: 100, message: 'Timezone must be at most 100 characters' },
        },
      },
      is_active: {
        type: 'checkbox',
        label: 'Active',
        helperText: 'Enable or disable this schedule',
        order: 6,
        section: 'scheduling',
      },
      input_data: {
        type: 'textarea',
        label: 'Input Data (JSON)',
        placeholder: '{}',
        helperText: 'JSON object passed as input to the workflow execution',
        order: 7,
        section: 'advanced',
        validation: {
          max: { value: 10000, message: 'Input data must be at most 10000 characters' },
        },
      },
      options: {
        type: 'textarea',
        label: 'Options (JSON)',
        placeholder: '{}',
        helperText: 'JSON object with additional schedule options',
        order: 8,
        section: 'advanced',
        validation: {
          max: { value: 10000, message: 'Options must be at most 10000 characters' },
        },
      },
    },

    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        order: 1,
      },
      {
        id: 'scheduling',
        title: 'Schedule Configuration',
        order: 2,
      },
      {
        id: 'advanced',
        title: 'Advanced',
        order: 3,
      },
    ],

    actions: createFormActions('Schedule', isEdit, { alignment: 'right' }),

    layout: createFormLayout(),

    workflow: createFormWorkflow(),

    i18n: createFormI18n('Schedule', isEdit),
  };
}
